<script lang="ts" generic="TOption" setup>
import debounce from 'lodash-es/debounce';
import { Multiselect } from 'vue-multiselect';

type ValueType = null | string | number | string[] | number[];

const props = withDefaults(defineProps<{
    name?: string;
    trackBy?: string;
    label?: string;
    placeholder?: string;
    multiple?: boolean;
    disabled?: boolean;
    refresh?: boolean;
    limit?: number;
    modelValue: ValueType;
    defaultLabel?: string;
    noEmptySearches?: boolean;
    fetch: (id: any) => Promise<TOption>;
    search: (query: string, limit: number) => Promise<TOption[]>;
}>(), {
    name: '',
    trackBy: 'value',
    label: 'text',
    placeholder: '',
    multiple: false,
    disabled: false,
    refresh: false,
    limit: 10,
    defaultLabel: null,
    noEmptySearches: false
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: ValueType): void;
    (e: 'changed', value: null | Record<string, any> | Record<string, any>[]): void;
    (e: 'enter', option: any): void;
}>();

const isOpen = ref(false);
const activeOption = ref<Record<string, any> | null>(null);
const multiselect = ref(null);
const loading = ref(false);
const options = ref<Record<string, any>[]>([]);
const selected = ref<null | Record<string, any> | Record<string, any>[]>(null);
const valueModel = computed<number[]>({
    get: (): number[] =>
    {
        return props.multiple
            ? (props.modelValue ?? []) as number[]
            : props.modelValue
                ? [props.modelValue as number]
                : [];
    },
    set: (value: number[]) =>
    {
        if (props.multiple)
            emit('update:modelValue', value);
        else
            emit('update:modelValue', value.first());

        emit('changed', selected.value);

        multiselect.value.$el.querySelector('input').dispatchEvent(new Event('input', {
            bubbles: true
        }));
    }
});
const selectedModel = computed<Record<string, any>[]>({
    get: (): Record<string, any>[] =>
    {
        return props.multiple
            ? (selected.value ?? []) as Record<string, any>[]
            : selected.value != null
                ? [selected.value as Record<string, any>]
                : [];
    },
    set: (value: Record<string, any>[]) =>
    {
        if (props.multiple)
            selected.value = value;
        else
            selected.value = value.first();
    }
});

const fetchModel = async (values: number[]): Promise<void> =>
{
    if (values.none())
    {
        selectedModel.value = [];
    }

    values.forEach((value) =>
    {
        if (value != null && selectedModel.value.where(p => props.trackBy in p && p[props.trackBy] == value).none())
        {
            props.fetch(value).then((result) =>
            {
                if (result != null)
                {
                    if (props.multiple)
                        selectedModel.value = [...selectedModel.value, result];
                    else
                        selectedModel.value = [result];
                }
            });
        }
    });
};

const searchOptions = async (query: string): Promise<void> =>
{
    loading.value = true;
    options.value = await props.search(query, props.limit);
    loading.value = false;
};

const onSearchChanged = debounce(async (query: string): Promise<void> =>
{
    if (props.noEmptySearches && query.length < 3) return;

    await searchOptions(query);
},
500);

function getKey(o: any)
{
    const key = props.trackBy ?? 'id';

    return o?.[key];
}

function selectAndEmit(opt: any)
{
    if (!opt) return;

    if (props.multiple)
    {
        if (selectedModel.value.every(x => getKey(x) !== getKey(opt)))
        {
            selectedModel.value = [...selectedModel.value, opt];
        }
    }
    else
    {
        selectedModel.value = [opt];
    }

    multiselect.value?.deactivate?.();

    emit('enter', opt);
}

function onOptionClick(opt: any)
{
    selectAndEmit(opt);
}

function selectActiveOptionAndEmit()
{
    const opt = activeOption.value;

    if (!opt) return;

    if (props.multiple)
    {
        if (selectedModel.value.every(x => getKey(x) !== getKey(opt)))
        {
            selectedModel.value = [...selectedModel.value, opt];
        }
    }
    else
    {
        selectedModel.value = [opt];
    }

    multiselect.value?.deactivate?.();

    emit('enter', opt);
}

function onKeydown(e: KeyboardEvent)
{
    if (!isOpen.value) return;

    if (e.key === 'Enter')
    {
        e.preventDefault();
        e.stopPropagation();
        selectActiveOptionAndEmit();
    }
}

watch(selectedModel, (value, old) =>
{
    const c1 = value.select(p => props.trackBy in p && p[props.trackBy]).orderBy(p => p).toArray();
    const c2 = (old ?? []).select(p => props.trackBy in p && p[props.trackBy]).orderBy(p => p).toArray();

    if (c1.toString() != c2.toString())
    {
        valueModel.value = value.select(p => props.trackBy in p && p[props.trackBy]).toArray();
    }
});

watch(valueModel, (value) =>
{
    fetchModel(value);
});

watch(() => props.disabled, (value) =>
{
    if (value == false)
    {
        fetchModel(valueModel.value);
    }
});

onMounted(() =>
{
    fetchModel(valueModel.value);
});

const clear = (): void =>
{
    selectedModel.value = [];
    multiselect.value.deactivate();
};

const open = async (): Promise<void> =>
{
    if (props.noEmptySearches) return;

    isOpen.value = true;
    window.addEventListener('keydown', onKeydown, true);

    if (props.refresh || options.value.length == 0)
    {
        await searchOptions('');
    }
};

const close = (): void =>
{
    isOpen.value = false;
    window.removeEventListener('keydown', onKeydown, true);
    activeOption.value = null;

    if (props.refresh)
    {
        if (selectedModel.value.none())
        {
            options.value = [];
        }
        else
        {
            options.value = selectedModel.value;
        }
    }
};
</script>

<template>
    <Multiselect
        ref="multiselect"
        v-model="selected"
        :options="options"
        :name="props.name"
        :label="props.label"
        :track-by="props.trackBy"
        :loading="loading"
        :placeholder="props.placeholder || $t('[[[wybierz...]]]')"
        :select-label="''"
        :selected-label="''"
        :deselect-label="''"
        :multiple="props.multiple"
        :searchable="true"
        :options-limit="props.limit"
        :internal-search="false"
        :clear-on-select="false"
        :close-on-select="true"
        :max-height="300"
        :show-no-results="true"
        :hide-selected="false"
        :disabled="props.disabled"
        :default-label="props.defaultLabel"
        @search-change="onSearchChanged"
        @open="open()"
        @close="close()"
    >
        <template #noOptions>
            <div class="text-center">
                {{ $t('[[[Lista jest pusta]]]') }}
            </div>
        </template>
        <template #noResult>
            <div class="text-center">
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </div>
        </template>
        <template #singleLabel="{ option }">
            <slot name="selected" :option="option" />
        </template>
        <template #option="{ option }">
            <div
                class="ideo-option-hover"
                @mouseover="activeOption = option"
                @click.stop.prevent="onOptionClick(option)"
            >
                <slot name="option" :option="option" />
            </div>
        </template>
        <template #beforeList>
            <li class="multiselect__element" @click="clear">
                <span class="multiselect__option empty">{{ props.defaultLabel || '&nbsp;' }}</span>
            </li>
        </template>
    </Multiselect>
</template>
